import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getProductsService, getCategoriesService, storeProductService, deleteProductService, updateProductService, getOrdersService, getEmailTemplatesService, getFreeTapesService, updateFreeTapeStatusService, updateEmailTemplateService, toggleEmailTemplateService, exportOrdersService, deleteOrderService, updateOrderStatusService, getProductVariantsService, updateVariantQuantityService } from "../Services/ProductService";

const getPaginationFromResponse = (responseData, fallbackLength = 0, perPage = 12, listSource = null) => {
  const pagination =
    responseData?.data?.pagination ||
    listSource?.pagination ||
    (listSource?.total !== undefined ? listSource : null) ||
    responseData?.pagination ||
    responseData?.meta?.pagination ||
    responseData?.meta;

  if (pagination) {
    const normalizedPerPage = Number(pagination.per_page || perPage) || perPage;
    const totalItems =
      pagination.total_items ??
      pagination.total ??
      pagination.total_count ??
      pagination.count ??
      fallbackLength;
    const totalPages =
      pagination.total_pages ??
      pagination.last_page ??
      Math.max(1, Math.ceil((Number(totalItems) || 0) / normalizedPerPage));

    return {
      ...pagination,
      current_page: pagination.current_page || 1,
      per_page: normalizedPerPage,
      total_items: totalItems,
      total_pages: totalPages,
    };
  }

  return {
    current_page: 1,
    per_page: perPage,
    total_items: fallbackLength,
    total_pages: Math.max(1, Math.ceil(fallbackLength / perPage)),
  };
};

const getListData = (...sources) => {
  for (const source of sources) {
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
  }

  return [];
};

// Get Products Thunk
export const getProducts = createAsyncThunk(
  "products/getProducts",
  async (
    { page = 1, perPage = 12, search = "", category = "" } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await getProductsService(page, perPage, search, category);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch products");
    }
  },
);

// Get Categories Thunk
export const getCategories = createAsyncThunk(
  "categories/getCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCategoriesService();
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch categories");
    }
  }
);

// Store Product Thunk
export const storeProduct = createAsyncThunk(
  "products/storeProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const response = await storeProductService(productData);
      return response;
    } catch (error) {
      const payload = error?.response?.data || error?.message || "Failed to store product";
      return rejectWithValue(payload);
    }
  }
);

// Delete Product Thunk
export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await deleteProductService(productId);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to delete product");
    }
  }
);

// Update Product Thunk
export const updateProduct = createAsyncThunk(
  "products/updateProduct",
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const response = await updateProductService(productId, productData);
      return response;
    } catch (error) {
      const payload = error?.response?.data || error?.message || "Failed to update product";
      return rejectWithValue(payload);
    }
  }
);

// Get Orders Thunk
export const getOrders = createAsyncThunk(
  "products/getOrders",
  async ({ page, perPage, search = "" } = {}, { rejectWithValue }) => {
    try {
      const response = await getOrdersService(page, perPage, search);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch orders");
    }
  }
);

// Get Email Templates Thunk
export const getEmailTemplates = createAsyncThunk(
  "products/getEmailTemplates",
  async ({ page, perPage } = {}, { rejectWithValue }) => {
    try {
      const response = await getEmailTemplatesService(page, perPage);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch email templates");
    }
  }
);

// Get Free Tapes Thunk
export const getFreeTapes = createAsyncThunk(
  "products/getFreeTapes",
  async ({ page, perPage, search = "" } = {}, { rejectWithValue }) => {
    try {
      const response = await getFreeTapesService(page, perPage, search);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch free tapes");
    }
  }
);

// Update Free Tape Status Thunk
export const updateFreeTapeStatus = createAsyncThunk(
  "products/updateFreeTapeStatus",
  async ({ tapeId, status }, { rejectWithValue }) => {
    try {
      const response = await updateFreeTapeStatusService(tapeId, status);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to update free tape status");
    }
  }
);

// Update Email Template Thunk
export const updateEmailTemplate = createAsyncThunk(
  "products/updateEmailTemplate",
  async ({ key, templateData }, { rejectWithValue }) => {
    try {
      const response = await updateEmailTemplateService(key, templateData);
      return response;
    } catch (error) {
      const payload =
        error?.response?.data || error?.message || "Failed to update email template";
      return rejectWithValue(payload);
    }
  }
);

export const toggleEmailTemplate = createAsyncThunk(
  "products/toggleEmailTemplate",
  async ({ key, active }, { rejectWithValue }) => {
    try {
      return await toggleEmailTemplateService(key, active);
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to update template status");
    }
  }
);

// Export Orders CSV Thunk
export const exportOrders = createAsyncThunk(
  "products/exportOrders",
  async ({ status = "" } = {}, { rejectWithValue }) => {
    try {
      const response = await exportOrdersService(status);
      const csvText = await response.blob.text();
      const updatedCsvText = csvText.replace(/\bdelivered\b/gi, "shipped");
      const updatedBlob = new Blob([updatedCsvText], { type: "text/csv;charset=utf-8;" });
      const downloadUrl = window.URL.createObjectURL(updatedBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = response.filename || "orders.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      return { success: true };
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to export orders");
    }
  }
);

// Delete Order Thunk
export const deleteOrder = createAsyncThunk(
  "products/deleteOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await deleteOrderService(orderId);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to delete order");
    }
  }
);

// Update Order Status Thunk
export const updateOrderStatus = createAsyncThunk(
  "products/updateOrderStatus",
  async ({ orderId, status }, { rejectWithValue }) => {
    try {
      const response = await updateOrderStatusService(orderId, status);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to update order status");
    }
  }
);

// Get Product Variants Thunk
export const getProductVariants = createAsyncThunk(
  "products/getProductVariants",
  async ({ page, perPage, productId } = {}, { rejectWithValue }) => {
    try {
      const response = await getProductVariantsService(page, perPage, productId);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch product variants");
    }
  }
);

// Update Variant Quantity Thunk
export const updateVariantQuantity = createAsyncThunk(
  "products/updateVariantQuantity",
  async ({ variantId, quantity }, { rejectWithValue }) => {
    try {
      const response = await updateVariantQuantityService(variantId, quantity);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to update variant quantity");
    }
  }
);

const productSlice = createSlice({
  name: "products",
  initialState: {
    products: [],
    categories: [],
    orders: [],
    emailTemplates: [],
    updatingEmailTemplateKey: null,
    freeTapes: [],
    variants: [],
    ordersPagination: {
      current_page: 1,
      per_page: 12,
      total_items: 0,
      total_pages: 1,
    },
    emailTemplatesPagination: {
      current_page: 1,
      per_page: 12,
      total_items: 0,
      total_pages: 1,
    },
    freeTapesPagination: {
      current_page: 1,
      per_page: 12,
      total_items: 0,
      total_pages: 1,
    },
    variantsPagination: {
      current_page: 1,
      per_page: 12,
      total_items: 0,
      total_pages: 1,
    },
    loading: false,
    error: null,
    submitError: null,
    deletingOrder: false,
    deletingProduct: false,
    updatingQuantity: false,
    updatingOrderStatus: false,
    updatingFreeTapeStatus: false,
    pagination: {
      current_page: 1,
      per_page: 12,
      total_items: 0,
      total_pages: 1,
    },
  },
  reducers: {
    clearProducts: (state) => {
      state.products = [];
      state.error = null;
    },
    clearCategories: (state) => {
      state.categories = [];
      state.error = null;
    },
    clearSubmitError: (state) => {
      state.submitError = null;
    },
    clearOrders: (state) => {
      state.orders = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Products
      .addCase(getProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        
        if (responseData?.success && responseData?.data) {
          state.products = responseData.data.products || [];
          state.categories = responseData.data.filters?.categories || state.categories;
          if (responseData.data.pagination) {
            state.pagination = responseData.data.pagination;
          }
        } else {
          state.products = [];
        }
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.products = [];
      })

    //   Get Ctegories Cases
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        
        if (responseData?.success && responseData?.data) {
          state.categories = responseData.data.categories || responseData.data || [];
        } else if (Array.isArray(responseData)) {
          state.categories = responseData;
        } else {
          state.categories = [];
        }
      })
      .addCase(getCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.categories = [];
      })
      
      // Store Product
      .addCase(storeProduct.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(storeProduct.fulfilled, (state, action) => {
        state.submitting = false;
      })
      .addCase(storeProduct.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload;
      })

      // Delete Product Cases
      .addCase(deleteProduct.pending, (state) => {
        state.deletingProduct = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.deletingProduct = false;
        const deletedProductId = action.meta.arg;
        state.products = state.products.filter(
          (product) => String(product.id) !== String(deletedProductId)
        );
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.deletingProduct = false;
        state.error = action.payload;
      })

      // Update Product Cases
      .addCase(updateProduct.pending, (state) => {
        state.updating = true;
        state.submitError = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.updating = false;
        const updatedProduct = action.payload?.data?.product || action.payload?.data;
        if (updatedProduct) {
          const index = state.products.findIndex(p => p.id === updatedProduct.id);
          if (index !== -1) {
            state.products[index] = { ...state.products[index], ...updatedProduct };
          }
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.updating = false;
        state.submitError = action.payload;
      })

      // Get Orders Cases
      .addCase(getOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        
        if (responseData?.success && responseData?.data) {
          state.orders = getListData(responseData.data.orders);
          state.ordersPagination = getPaginationFromResponse(
            responseData,
            state.orders.length,
            action.meta.arg?.perPage || 12,
            responseData.data.orders
          );
        } else if (Array.isArray(responseData)) {
          state.orders = responseData;
          state.ordersPagination = getPaginationFromResponse(
            responseData,
            responseData.length,
            action.meta.arg?.perPage || 12
          );
        } else {
          state.orders = [];
          state.ordersPagination = getPaginationFromResponse(
            responseData,
            0,
            action.meta.arg?.perPage || 12
          );
        }
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.orders = [];
      })

      // Get Email Templates Cases
      .addCase(getEmailTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEmailTemplates.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;

        if (responseData?.success && responseData?.data) {
          state.emailTemplates = getListData(responseData.data.templates);
          state.emailTemplatesPagination = getPaginationFromResponse(
            responseData,
            state.emailTemplates.length,
            action.meta.arg?.perPage || 12,
            responseData.data.templates
          );
        } else {
          state.emailTemplates = [];
          state.emailTemplatesPagination = getPaginationFromResponse(
            responseData,
            0,
            action.meta.arg?.perPage || 12
          );
        }
      })
      .addCase(getEmailTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.emailTemplates = [];
      })

      // Get Free Tapes Cases
      .addCase(getFreeTapes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFreeTapes.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;

        if (responseData?.success && responseData?.data) {
          state.freeTapes =
            getListData(
              responseData.message,
              responseData.data.free_tapes,
              responseData.data.freeTapes,
              responseData.data.tapes,
              responseData.data.requests,
              responseData.data
            );
          state.freeTapesPagination = getPaginationFromResponse(
            responseData,
            state.freeTapes.length,
            action.meta.arg?.perPage || 12,
            responseData.data.free_tapes ||
              responseData.data.freeTapes ||
              responseData.data.tapes ||
              responseData.data.requests ||
              responseData.data
          );
        } else if (Array.isArray(responseData?.message)) {
          state.freeTapes = responseData.message;
          state.freeTapesPagination = getPaginationFromResponse(
            responseData,
            responseData.message.length,
            action.meta.arg?.perPage || 12
          );
        } else if (Array.isArray(responseData)) {
          state.freeTapes = responseData;
          state.freeTapesPagination = getPaginationFromResponse(
            responseData,
            responseData.length,
            action.meta.arg?.perPage || 12
          );
        } else {
          state.freeTapes = [];
          state.freeTapesPagination = getPaginationFromResponse(
            responseData,
            0,
            action.meta.arg?.perPage || 12
          );
        }
      })
      .addCase(getFreeTapes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.freeTapes = [];
      })

      // Update Free Tape Status Cases
      .addCase(updateFreeTapeStatus.pending, (state) => {
        state.updatingFreeTapeStatus = true;
        state.error = null;
      })
      .addCase(updateFreeTapeStatus.fulfilled, (state, action) => {
        state.updatingFreeTapeStatus = false;
        const updatedTape =
          action.payload?.data?.free_tape ||
          action.payload?.data?.freeTape ||
          action.payload?.data?.tape ||
          action.payload?.data;
        const tapeId = action.meta.arg.tapeId;
        const status = action.meta.arg.status;

        const index = state.freeTapes.findIndex(
          (tape) => String(tape.id ?? tape.free_tape_id) === String(tapeId)
        );

        if (index !== -1) {
          state.freeTapes[index] = {
            ...state.freeTapes[index],
            status,
            ...updatedTape,
          };
        }
      })
      .addCase(updateFreeTapeStatus.rejected, (state, action) => {
        state.updatingFreeTapeStatus = false;
        state.error = action.payload;
      })

      // Update Email Template Cases
      .addCase(updateEmailTemplate.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(updateEmailTemplate.fulfilled, (state, action) => {
        state.submitting = false;
        const updatedTemplate = action.payload?.data?.template || action.payload?.data;
        const templateKey = action.meta.arg.key;
        if (updatedTemplate) {
          const index = state.emailTemplates.findIndex(
            (template) => template.key === templateKey || template.id === updatedTemplate.id
          );
          if (index !== -1) {
            state.emailTemplates[index] = {
              ...state.emailTemplates[index],
              ...updatedTemplate,
            };
          }
        }
      })
      .addCase(updateEmailTemplate.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload;
      })

      .addCase(toggleEmailTemplate.pending, (state, action) => {
        state.updatingEmailTemplateKey = action.meta.arg.key;
        state.error = null;
      })
      .addCase(toggleEmailTemplate.fulfilled, (state, action) => {
        const { key, active } = action.meta.arg;
        state.updatingEmailTemplateKey = null;
        const index = state.emailTemplates.findIndex((template) => template.key === key);
        if (index !== -1) {
          state.emailTemplates[index].active =
            action.payload?.data?.template?.active ??
            action.payload?.data?.active ??
            active;
        }
      })
      .addCase(toggleEmailTemplate.rejected, (state, action) => {
        state.updatingEmailTemplateKey = null;
        state.error = action.payload;
      })

      // Delete Order Cases
      .addCase(deleteOrder.pending, (state) => {
        state.deletingOrder = true;
        state.error = null;
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.deletingOrder = false;
        const deletedOrderId = action.meta.arg;
        state.orders = state.orders.filter(
          (order) =>
            String(order.id ?? order.order_id) !== String(deletedOrderId)
        );
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.deletingOrder = false;
        state.error = action.payload;
      })

      // Update Order Status Cases
      .addCase(updateOrderStatus.pending, (state) => {
        state.updatingOrderStatus = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.updatingOrderStatus = false;
        const updatedOrder = action.payload?.data?.order || action.payload?.data;
        const orderId = action.meta.arg.orderId; // Get the orderId from the thunk argument

        if (updatedOrder || orderId) {
          const index = state.orders.findIndex(o => o.id === orderId || o.order_id === orderId);
          if (index !== -1) {
            // Update the order with the new data, specifically the status
            state.orders[index] = {
              ...state.orders[index],
              status: action.meta.arg.status, // Update status from the argument
              ...updatedOrder // Merge any additional data from response
            };
          }
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.updatingOrderStatus = false;
        state.error = action.payload;
      })

      // Get Product Variants Cases
      .addCase(getProductVariants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductVariants.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        
        if (responseData?.data) {
          state.variants = getListData(responseData.data.variants, responseData.data);
          state.variantsPagination = getPaginationFromResponse(
            responseData,
            state.variants.length,
            action.meta.arg?.perPage || 12,
            responseData.data.variants || responseData.data
          );
        } else if (Array.isArray(responseData)) {
          state.variants = responseData;
          state.variantsPagination = getPaginationFromResponse(
            responseData,
            responseData.length,
            action.meta.arg?.perPage || 12
          );
        } else {
          state.variants = [];
          state.variantsPagination = getPaginationFromResponse(
            responseData,
            0,
            action.meta.arg?.perPage || 12
          );
        }
      })
      .addCase(getProductVariants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.variants = [];
      })

      // Update Variant Quantity Cases
      .addCase(updateVariantQuantity.pending, (state) => {
        state.updatingQuantity = true;
        state.error = null;
      })
      .addCase(updateVariantQuantity.fulfilled, (state, action) => {
        state.updatingQuantity = false;
        const updatedVariant = action.payload?.data?.variant || action.payload?.data;
        if (updatedVariant) {
          const index = state.variants.findIndex(v => v.id === updatedVariant.id);
          if (index !== -1) {
            state.variants[index] = { ...state.variants[index], ...updatedVariant };
          }
        }
      })
      .addCase(updateVariantQuantity.rejected, (state, action) => {
        state.updatingQuantity = false;
        state.error = action.payload;
      })
  },
});

export const { clearProducts, clearCategories, clearSubmitError, clearOrders } = productSlice.actions;
export default productSlice.reducer;
